import React, { useState, useEffect } from 'react';
import { X, Edit, Trash2, Users, Eye, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import api from '../api';
import { toast } from 'react-toastify';

const NotificationDetail = ({ notificationId, onClose, onEdit, onDelete }) => {
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotificationDetail();
    
    // Auto-refresh toutes les 3 secondes pour voir les lectures en temps réel
    const intervalId = setInterval(() => {
      fetchNotificationDetail();
    }, 3000);
    
    return () => clearInterval(intervalId);
  }, [notificationId]);

  const fetchNotificationDetail = async () => {
    try {
      const response = await api.get(`/api/utilisateur/admin/notifications/${notificationId}/`);
      setNotification(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      if (loading) {
        toast.error('Erreur lors du chargement des détails');
      }
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      INFO: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '📢' },
      AVERTISSEMENT: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⚠️' },
      URGENCE: { bg: 'bg-red-100', text: 'text-red-800', icon: '🚨' },
      FELICITATION: { bg: 'bg-green-100', text: 'text-green-800', icon: '🎉' },
      RAPPEL: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '⏰' },
      EVENEMENT: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: '📅' }
    };
    return badges[type] || badges.INFO;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      BASSE: 'bg-gray-100 text-gray-800',
      NORMALE: 'bg-blue-100 text-blue-800',
      HAUTE: 'bg-orange-100 text-orange-800',
      CRITIQUE: 'bg-red-100 text-red-800'
    };
    return badges[priority] || badges.NORMALE;
  };

  const getStatusLabel = (notification) => {
    if (notification.est_envoyee) return 'ENVOYÉE';
    if (notification.est_programmee) return 'PROGRAMMÉE';
    return 'BROUILLON';
  };

  const getStatusIcon = (notification) => {
    if (notification.est_envoyee) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (notification.est_programmee) return <Clock className="w-4 h-4 text-blue-600" />;
    return <AlertCircle className="w-4 h-4 text-gray-600" />;
  };

  const getTauxLecture = () => {
    if (!notification.nombre_destinataires || notification.nombre_destinataires === 0) return 0;
    return ((notification.nombre_lectures || 0) / notification.nombre_destinataires * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B00]"></div>
        </div>
      </div>
    );
  }

  if (!notification) return null;

  const typeBadge = getTypeBadge(notification.type_notification);
  const statut = getStatusLabel(notification);
  const statusIcon = getStatusIcon(notification);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col my-8">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Détails de la notification</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${typeBadge.bg} ${typeBadge.text}`}>
              {typeBadge.icon} {notification.type_notification}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityBadge(notification.priorite)}`}>
              {notification.priorite}
            </span>
            {notification.est_importante && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                ⭐ Importante
              </span>
            )}
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{notification.titre}</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{notification.message}</p>
          </div>

          {notification.est_envoyee && notification.nombre_destinataires > 0 && (
            <div className="bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg p-6">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Statistiques de lecture
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-white/80 text-sm">Destinataires</p>
                  <p className="text-2xl font-bold">{notification.nombre_destinataires}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Lectures</p>
                  <p className="text-2xl font-bold">{notification.nombre_lectures || 0}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Taux de lecture</p>
                  <p className="text-2xl font-bold">{getTauxLecture()}%</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Type de cible</p>
              <p className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                {notification.type_cible_display || notification.type_cible}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Statut</p>
              <p className="font-medium flex items-center gap-2">
                {statusIcon}
                {statut}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Date de création</p>
              <p className="font-medium">
                {new Date(notification.created_at).toLocaleString('fr-FR')}
              </p>
            </div>

            {notification.date_envoi && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Date d'envoi</p>
                <p className="font-medium">
                  {new Date(notification.date_envoi).toLocaleString('fr-FR')}
                </p>
              </div>
            )}

            {notification.date_envoi_programmee && !notification.est_envoyee && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Envoi programmé le</p>
                <p className="font-medium">
                  {new Date(notification.date_envoi_programmee).toLocaleString('fr-FR')}
                </p>
              </div>
            )}

            {notification.expiration && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Expire le</p>
                <p className="font-medium">
                  {new Date(notification.expiration).toLocaleString('fr-FR')}
                </p>
              </div>
            )}
          </div>

          {notification.lien && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-600 mb-2 font-medium">Lien associé</p>
              <a
                href={notification.lien}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {notification.lien}
              </a>
            </div>
          )}

          <div>
            <h4 className="font-semibold mb-3">Options d'envoi</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={notification.envoyer_push} disabled className="w-4 h-4" />
                <span className="text-gray-700">Notification push</span>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={notification.envoyer_email} disabled className="w-4 h-4" />
                <span className="text-gray-700">Email</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t p-6 bg-gray-50 flex gap-3">
          {!notification.est_envoyee && (
            <button
              onClick={() => onEdit(notification)}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              <Edit className="w-5 h-5" />
              Modifier
            </button>
          )}
          
          <button
            onClick={() => {
              onDelete(notification.id);
              onClose();
            }}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
          >
            <Trash2 className="w-5 h-5" />
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationDetail;
